import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Activity, Paperclip } from 'lucide-react';
import type { ThreadMessageAttachment } from '@/types';
import type { ResolvedComposerConfig } from '@/types/composer';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  collectClipboardAttachableFiles,
  COMPOSER_FILE_ACCEPT,
  fileListFromFiles,
  isComposerAttachableFile,
  isEditableElement,
} from '@/lib/composer-clipboard';
import { cn } from '@/lib/utils';
import { StagedAttachmentChip } from './attachment-chip';
import { ComposerCapabilitiesStrip } from './composer-capabilities-strip';
import { ComposerFocusChip } from './composer-focus-chip';
import { AnimatedSendButton } from '../animated-send-button';
import {
  getComposerBandInnerClassName,
  getComposerBandOuterClassName,
} from './chat-message-styles';

export interface ChatInputHandle {
  focus: () => void;
}

/** Visible textarea lines before content scrolls inside the field. */
export const COMPOSER_MAX_VISIBLE_LINES = 8;

function measureComposerTextareaHeights(textarea: HTMLTextAreaElement): {
  minHeight: number;
  maxHeight: number;
} {
  const styles = getComputedStyle(textarea);
  const lineHeight = parseFloat(styles.lineHeight);
  const paddingTop = parseFloat(styles.paddingTop);
  const paddingBottom = parseFloat(styles.paddingBottom);
  const linePx = Number.isFinite(lineHeight) && lineHeight > 0 ? lineHeight : 20;
  const paddingY = paddingTop + paddingBottom;
  return {
    minHeight: linePx + paddingY,
    maxHeight: linePx * COMPOSER_MAX_VISIBLE_LINES + paddingY,
  };
}

function syncComposerTextareaHeight(textarea: HTMLTextAreaElement): void {
  textarea.style.height = 'auto';
  const { minHeight, maxHeight } = measureComposerTextareaHeights(textarea);
  const contentHeight = textarea.scrollHeight;
  const nextHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
  textarea.style.height = `${nextHeight}px`;
  textarea.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden';
}

/**
 * Deliberate hero (welcome, `centered`) vs compact (in-chat) scale differences,
 * consolidated in one place. Textarea grows 1→8 lines then scrolls; only padding
 * and placeholder scale differ between hero and compact.
 */
const COMPOSER_SCALE = {
  hero: {
    textarea: 'px-2.5 py-2.5',
    placeholder: 'placeholder:text-sm',
    chipsRow: 'px-2.5 pb-2',
    toolbar: 'px-2.5 pb-2.5',
    indicatorIcon: 'h-4 w-4',
  },
  compact: {
    textarea: 'px-2.5 py-2.5',
    placeholder: 'placeholder:text-xs',
    chipsRow: 'px-2.5 pb-2',
    toolbar: 'px-2.5 pb-2.5',
    indicatorIcon: 'h-3.5 w-3.5',
  },
} as const;

interface ChatInputProps {
  mini?: boolean;
  onMiniFocus?: () => void;
  inputText: string;
  setInputText: (text: string) => void;
  onSubmit: () => void;
  isLoading?: boolean;
  /** Hero scale when true (welcome empty-chat); compact scale in the in-chat thread dock. See docs/DESIGN_SYSTEM.md § Chat composer modes. */
  centered?: boolean;
  clientName?: string;
  composerPlaceholder?: string;
  placeholderPreview?: string | null;
  composerConfig: ResolvedComposerConfig;
  capabilitiesSummary?: string;
  capabilitiesStreaming?: boolean;
  agentCapabilities?: string[];
  focusKey?: string | null;
  showConnectionIndicator?: boolean;
  connectionConnected?: boolean;
  pendingAttachments?: ThreadMessageAttachment[];
  onAttachFiles?: (files: FileList) => void;
  /** Remove a staged attachment chip rendered inside the composer shell. */
  onRemoveAttachment?: (id: string) => void;
  /** Capability pill strip renders directly above — tighten composer top padding. */
  capabilityStripAbove?: boolean;
}

/** Memoized: slotted renders (e.g. parent layout updates) skip when props are unchanged. */
export const ChatInput = memo(forwardRef<ChatInputHandle, ChatInputProps>(function ChatInput(
  {
    mini = false,
    onMiniFocus,
    inputText,
    setInputText,
    onSubmit,
    isLoading = false,
    centered = false,
    clientName = 'agent',
    composerPlaceholder,
    placeholderPreview = null,
    composerConfig,
    capabilitiesSummary = '',
    capabilitiesStreaming = false,
    agentCapabilities = [],
    focusKey = null,
    showConnectionIndicator = false,
    connectionConnected = false,
    pendingAttachments = [],
    onAttachFiles,
    onRemoveAttachment,
    capabilityStripAbove = false,
  },
  ref
) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const composerRootRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const touchLastYRef = useRef(0);
  const dragDepthRef = useRef(0);
  const [composerFocused, setComposerFocused] = useState(false);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const isMobile = useIsMobile();

  useImperativeHandle(ref, () => ({
    focus: () => {
      textareaRef.current?.focus();
    },
  }));

  const placeholder =
    placeholderPreview?.trim() ||
    composerPlaceholder?.trim() ||
    `Message ${clientName}…`;

  const canSend = Boolean(
    (inputText.trim() || pendingAttachments.length > 0) &&
      !pendingAttachments.some((a) => a.isUploading)
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
        e.preventDefault();
        if (canSend) onSubmit();
      }
    },
    [onSubmit, isLoading, canSend]
  );

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const frame = requestAnimationFrame(() => {
      syncComposerTextareaHeight(textarea);
    });

    return () => cancelAnimationFrame(frame);
  }, [inputText, centered]);

  useEffect(() => {
    if (!isMobile) return;
    const root = composerRootRef.current;
    if (!root) return;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches[0]) touchLastYRef.current = e.touches[0].clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      const ta = textareaRef.current;
      if (!ta || !e.touches[0]) return;
      const target = e.target;
      if (!(target instanceof Node)) return;
      const onTextarea = target === ta || ta.contains(target);

      if (!onTextarea) {
        e.preventDefault();
        return;
      }

      const scrollable = ta.scrollHeight > ta.clientHeight + 1;
      if (!scrollable) {
        e.preventDefault();
        return;
      }

      const y = e.touches[0].clientY;
      const dy = y - touchLastYRef.current;
      touchLastYRef.current = y;

      const st = ta.scrollTop;
      const sh = ta.scrollHeight;
      const ch = ta.clientHeight;
      const atTop = st <= 1;
      const atBottom = st + ch >= sh - 2;

      if (atTop && dy > 0) {
        e.preventDefault();
        return;
      }
      if (atBottom && dy < 0) {
        e.preventDefault();
      }
    };

    root.addEventListener('touchstart', onTouchStart, { passive: true });
    root.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      root.removeEventListener('touchstart', onTouchStart);
      root.removeEventListener('touchmove', onTouchMove);
    };
  }, [isMobile]);

  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const list = e.target.files;
      if (list?.length && onAttachFiles) {
        onAttachFiles(list);
      }
      e.target.value = '';
    },
    [onAttachFiles]
  );

  const attachClipboardFiles = useCallback(
    (clipboardData: DataTransfer | null | undefined): boolean => {
      if (!onAttachFiles || !clipboardData) return false;
      const files = collectClipboardAttachableFiles(clipboardData);
      if (!files.length) return false;
      const transferFiles = fileListFromFiles(files);
      if (!transferFiles) return false;
      onAttachFiles(transferFiles);
      return true;
    },
    [onAttachFiles]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (attachClipboardFiles(e.clipboardData)) {
        e.preventDefault();
      }
    },
    [attachClipboardFiles]
  );

  // Catch file paste when focus left the textarea (e.g. after Win+Shift+S / mobile keyboard).
  useEffect(() => {
    if (!onAttachFiles) return;

    const onDocumentPaste = (e: ClipboardEvent) => {
      if (e.defaultPrevented) return;

      const active = document.activeElement;
      // Textarea onPaste owns focus inside the composer; don't steal from other editables.
      if (active instanceof Element && isEditableElement(active)) {
        return;
      }

      if (attachClipboardFiles(e.clipboardData)) {
        e.preventDefault();
      }
    };

    document.addEventListener('paste', onDocumentPaste);
    return () => document.removeEventListener('paste', onDocumentPaste);
  }, [attachClipboardFiles, onAttachFiles]);

  const attachDroppedFiles = useCallback(
    (fileList: FileList | null) => {
      if (!onAttachFiles || !fileList?.length) return;
      const accepted = Array.from(fileList).filter(isComposerAttachableFile);
      const transferFiles = fileListFromFiles(accepted);
      if (transferFiles) {
        onAttachFiles(transferFiles);
      }
    },
    [onAttachFiles]
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (!onAttachFiles || !e.dataTransfer.types.includes('Files')) return;
      e.preventDefault();
      dragDepthRef.current += 1;
      setIsDraggingFiles(true);
    },
    [onAttachFiles]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!onAttachFiles || !e.dataTransfer.types.includes('Files')) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    [onAttachFiles]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDraggingFiles(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (!onAttachFiles) return;
      e.preventDefault();
      dragDepthRef.current = 0;
      setIsDraggingFiles(false);
      attachDroppedFiles(e.dataTransfer.files);
    },
    [attachDroppedFiles, onAttachFiles]
  );

  const showFocusChip =
    composerConfig.showFocusChip &&
    Boolean(focusKey) &&
    (composerConfig.philosophy === 'context' || composerConfig.philosophy === 'both');

  const showKeyboardHint =
    composerConfig.showKeyboardHint && !isMobile && (composerFocused || !inputText.trim());

  const shellClassName = cn(
    'relative w-full min-w-0 max-w-full rounded-[var(--radius-chat)] border border-border bg-background transition-shadow',
    composerConfig.style === 'elevated' &&
      'shadow-sm focus-within:ring-2 focus-within:ring-ring/40 focus-within:ring-offset-2 focus-within:ring-offset-background',
    isDraggingFiles &&
      'border-primary/60 ring-2 ring-primary/40 ring-offset-2 ring-offset-background'
  );

  const innerWidthClass = getComposerBandInnerClassName({ centered });

  const composerScale = COMPOSER_SCALE[centered ? 'hero' : 'compact'];

  const outerClassName = cn(
    getComposerBandOuterClassName({
      centered,
      alignWithDocumentBand: composerConfig.alignWithDocumentBand,
    }),
    centered ? 'my-0' : capabilityStripAbove ? 'pt-1' : 'pt-4',
    isMobile && 'overscroll-y-none'
  );

  const attachButton = onAttachFiles ? (
    <button
      type="button"
      disabled={isLoading}
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition-colors hover:bg-muted disabled:opacity-60"
      title="Attach file"
      aria-label="Attach file"
      onClick={() => fileInputRef.current?.click()}
    >
      <Paperclip className="h-4 w-4" aria-hidden />
    </button>
  ) : null;

  if (mini) {
    return (
      <div className={cn(shellClassName, 'flex items-center gap-2 rounded-full p-2')}>
        <input
          aria-label={`Message ${clientName}`}
          onFocus={onMiniFocus}
          value={inputText}
          onChange={(event) => setInputText(event.target.value)}
          onKeyDown={(event) => {
            if (!event.nativeEvent.isComposing) handleKeyDown(event);
          }}
          placeholder={placeholder}
          className="min-w-0 flex-1 border-0 bg-transparent px-2 py-2 text-base outline-none placeholder:text-muted-foreground"
        />
        <AnimatedSendButton retainPrimaryFill onClick={onSubmit} disabled={!canSend || isLoading} isLoading={false} />
      </div>
    );
  }

  const composerShell = (
    <div
      className={shellClassName}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDraggingFiles ? (
        <div
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[var(--radius-chat)] bg-primary/5"
          aria-hidden
        >
          <p className="text-sm font-medium text-primary">Drop files to attach</p>
        </div>
      ) : null}
      {composerConfig.showCapabilitiesStrip ? (
        <ComposerCapabilitiesStrip
          summary={capabilitiesSummary}
          isStreaming={capabilitiesStreaming}
          isLoading={isLoading}
          toolNames={agentCapabilities}
          centered={centered}
        />
      ) : null}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onFocus={() => setComposerFocused(true)}
          onBlur={() => setComposerFocused(false)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={placeholder}
          className={cn(
            'box-border min-w-0 w-full max-w-full resize-none border-0 bg-transparent shadow-none outline-none ring-0',
            'text-base leading-normal md:text-sm',
            composerScale.textarea,
            'break-words [overflow-wrap:anywhere] overscroll-y-contain placeholder:text-muted-foreground',
            composerScale.placeholder,
            'focus-visible:ring-0 focus-visible:ring-offset-0'
          )}
          rows={1}
        />
      </div>
      {pendingAttachments.length > 0 ? (
        <div
          className={cn('flex flex-wrap items-center gap-1.5', composerScale.chipsRow)}
          aria-label="Files staged for your next message"
        >
          {pendingAttachments.map((att) => (
            <StagedAttachmentChip
              key={att.id}
              attachment={att}
              onRemove={onRemoveAttachment}
            />
          ))}
        </div>
      ) : null}
      <div className={cn('flex items-center justify-between gap-2', composerScale.toolbar)}>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {showFocusChip && focusKey ? (
            <ComposerFocusChip focusKey={focusKey} className="max-w-[min(100%,10rem)]" />
          ) : null}
          {onAttachFiles ? (
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              multiple
              accept={COMPOSER_FILE_ACCEPT}
              onChange={onFileChange}
            />
          ) : null}
          {attachButton}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {showConnectionIndicator ? (
            <div
              className="flex shrink-0 items-center justify-center"
              title={connectionConnected ? 'Connected' : 'Disconnected'}
            >
              <Activity
                className={cn(
                  composerScale.indicatorIcon,
                  'shrink-0',
                  connectionConnected ? 'text-success' : 'text-destructive'
                )}
                aria-hidden
              />
              <span className="sr-only">
                {connectionConnected
                  ? 'Agent connection active'
                  : 'Agent connection unavailable'}
              </span>
            </div>
          ) : null}
          <AnimatedSendButton
            onClick={onSubmit}
            disabled={!canSend && !isLoading}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );

  const keyboardHint = showKeyboardHint ? (
    <p className="mt-1.5 text-center text-xs text-muted-foreground/80">
      Enter to send · Shift+Enter for newline
    </p>
  ) : null;

  return (
    <div ref={composerRootRef} className={outerClassName}>
      <div className={innerWidthClass}>
        {composerShell}
        {keyboardHint}
      </div>
    </div>
  );
}));
